// src/components/CreateTrailer.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import Select from "react-select";

import {
  useTranslation,
} from "react-i18next";

import {
  FaArrowLeft,
  FaBars,
  FaCalendarAlt,
  FaCheckCircle,
  FaDownload,
  FaEye,
  FaExclamationTriangle,
  FaFileAlt,
  FaFireExtinguisher,
  FaHashtag,
  FaIndustry,
  FaLayerGroup,
  FaLink,
  FaPlus,
  FaRoad,
  FaRulerCombined,
  FaRulerHorizontal,
  FaRulerVertical,
  FaShieldAlt,
  FaUpload,
  FaWeightHanging,
} from "react-icons/fa";

import axios from "../axiosConfig";

import "../styles/CreateTrailer.css";

/* =========================================================
   CONSTANTS
========================================================= */

const EMPTY_FORM_DATA = {
  trailerPlate: "",
  type: "",
  brand: "",
  month: "",
  year: "",

  height: "",
  width: "",
  length: "",
  weight: "",

  numberOfAxles: "",
  numberOfStraps: "",
  numberOfRatchets: "",
  numberOfWoodenBeams: "",
  numberOfEdgeProtectors: "",
  numberOfAntiSlipMats: "",
  numberOfSidebars: "",
  numberOfFireExtinguishers: "",

  inspectionExpiry: "",
  vehicleRegistrationCertificateExpiry: "",
  trailerInsuranceExpiry: "",
};

const EMPTY_FILES = {
  inspection: null,
  vehicleRegistrationCertificate: null,
  trailerInsurance: null,
};

const TRAILER_TYPES = [
  "Tautliner",
  "Van",
  "Open Box Truck",
  "Refrigerated Truck",
  "Vehicle Carrier",
  "Container Truck",
  "Clothing Hanging Carrier",
  "Cattle Carrier",
  "Dump Carrier",
  "Cistern",
  "Special Transport Truck",
  "Battery Carrier",
  "Flatbed Truck",
  "Box Truck",
  "Lowboy Truck",
];

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
];

const TRAILER_DETAILS_PREFIX =
  "trailer:detail:";

const TRAILER_DETAILS_TIMESTAMP_PREFIX =
  "trailer:detail:ts:";

const TRAILER_SUMMARY_CACHE_SUFFIX =
  ":trailers:summary:v2";

const TRAILER_SUMMARY_UPDATED_EVENT =
  "loadevo:trailer-summary-cache-updated";

const DOCUMENT_EXPIRY_WARNING_DAYS =
  60;

const DAY_MS =
  24 *
  60 *
  60 *
  1000;

/* =========================================================
   GENERIC HELPERS
========================================================= */

function safeParse(
  raw
) {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(
      raw
    );
  } catch {
    return null;
  }
}

function normalizePlate(
  value
) {
  return String(
    value ||
    ""
  )
    .trim()
    .toUpperCase();
}

function cleanFileName(
  value,
  fallback = "file"
) {
  if (!value) {
    return fallback;
  }

  let clean =
    String(
      value
    )
      .replace(
        /\\/g,
        "/"
      )
      .split(
        "#"
      )[0]
      .split(
        "?"
      )[0];

  let name =
    clean
      .split(
        "/"
      )
      .pop() ||
    fallback;

  try {
    name =
      decodeURIComponent(
        name
      );
  } catch {
    // Mantém o valor original.
  }

  return (
    name
      .replace(
        /[/\\?%*:|"<>]/g,
        "-"
      )
      .trim() ||
    fallback
  );
}

function fileName(
  file
) {
  if (!file) {
    return "";
  }

  if (
    typeof File !==
      "undefined" &&
    file instanceof File
  ) {
    return cleanFileName(
      file.name ||
      "file"
    );
  }

  if (
    typeof file ===
      "object"
  ) {
    return cleanFileName(
      file.name ||
      file.url ||
      "file"
    );
  }

  return cleanFileName(
    file
  );
}

function isPdfFile(
  file
) {
  if (!file) {
    return false;
  }

  if (
    typeof File !==
      "undefined" &&
    file instanceof File
  ) {
    return (
      file.type ===
        "application/pdf" ||
      /\.pdf$/i.test(
        file.name ||
        ""
      )
    );
  }

  return /\.pdf$/i.test(
    fileName(
      file
    )
  );
}

function isImageFile(
  file
) {
  if (!file) {
    return false;
  }

  if (
    typeof File !==
      "undefined" &&
    file instanceof File
  ) {
    return (
      String(
        file.type ||
        ""
      ).startsWith(
        "image/"
      ) ||
      /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(
        file.name ||
        ""
      )
    );
  }

  return /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(
    fileName(
      file
    )
  );
}

function daysUntilDate(
  value
) {
  if (!value) {
    return null;
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  date.setHours(
    0,
    0,
    0,
    0
  );

  return Math.ceil(
    (
      date.getTime() -
      today.getTime()
    ) /
    DAY_MS
  );
}

function getDocumentStatus(
  file,
  expiry,
  t
) {
  if (!file) {
    return {
      tone:
        "missing",

      label:
        t(
          "documents.status.missing",
          {
            defaultValue:
              "Em falta",
          }
        ),
    };
  }

  const days =
    daysUntilDate(
      expiry
    );

  if (
    days !== null &&
    days < 0
  ) {
    return {
      tone:
        "expired",

      label:
        t(
          "documents.status.expired",
          {
            defaultValue:
              "Expirado",
          }
        ),
    };
  }

  if (
    days !== null &&
    days <=
      DOCUMENT_EXPIRY_WARNING_DAYS
  ) {
    return {
      tone:
        "expiring",

      label:
        t(
          "documents.status.expiring",
          {
            defaultValue:
              "Em breve",
          }
        ),
    };
  }

  return {
    tone:
      "loaded",

    label:
      t(
        "documents.status.loaded",
        {
          defaultValue:
            "Carregado",
        }
      ),
  };
}

function unwrapCreatedTrailer(
  payload
) {
  const candidates = [
    payload?.trailer,
    payload?.data?.trailer,
    payload?.data,
    payload,
  ];

  return (
    candidates.find(
      (
        candidate
      ) => {
        return (
          candidate &&
          typeof candidate ===
            "object" &&
          !Array.isArray(
            candidate
          )
        );
      }
    ) ||
    {}
  );
}

function getSummaryItems(
  payload
) {
  if (
    Array.isArray(
      payload
    )
  ) {
    return payload;
  }

  if (
    Array.isArray(
      payload?.items
    )
  ) {
    return payload.items;
  }

  if (
    Array.isArray(
      payload?.rows
    )
  ) {
    return payload.rows;
  }

  if (
    Array.isArray(
      payload?.data?.items
    )
  ) {
    return payload.data.items;
  }

  return [];
}

function findCreatedTrailerInSummary(
  payload,
  plate
) {
  const wantedPlate =
    normalizePlate(
      plate
    );

  return (
    getSummaryItems(
      payload
    ).find(
      (
        trailer
      ) => {
        return (
          normalizePlate(
            trailer?.trailerPlate ??
            trailer?.trailerNumber
          ) ===
          wantedPlate
        );
      }
    ) ||
    null
  );
}

function writeTrailerDetailsCache(
  trailerId,
  trailer
) {
  if (
    typeof window ===
      "undefined" ||
    trailerId === null ||
    trailerId ===
      undefined ||
    trailerId === "" ||
    !trailer ||
    typeof trailer !==
      "object"
  ) {
    return;
  }

  try {
    window.localStorage.setItem(
      `${TRAILER_DETAILS_PREFIX}${trailerId}`,
      JSON.stringify(
        trailer
      )
    );

    window.localStorage.setItem(
      `${TRAILER_DETAILS_TIMESTAMP_PREFIX}${trailerId}`,
      String(
        Date.now()
      )
    );
  } catch {
    // Storage indisponível.
  }
}

function writeSummaryCacheItems(
  cacheKey,
  parsed,
  items
) {
  const now =
    Date.now();

  let nextValue;

  if (
    Array.isArray(
      parsed
    )
  ) {
    nextValue =
      items;
  } else if (
    parsed &&
    typeof parsed ===
      "object" &&
    parsed.data &&
    typeof parsed.data ===
      "object" &&
    Array.isArray(
      parsed.data.items
    )
  ) {
    nextValue = {
      ...parsed,

      savedAt:
        now,

      updatedAt:
        now,

      meta: {
        ...(
          parsed.meta ||
          {}
        ),

        etag:
          null,
      },

      data: {
        ...parsed.data,

        items,
      },
    };
  } else {
    nextValue = {
      ...(
        parsed &&
        typeof parsed ===
          "object"
          ? parsed
          : {}
      ),

      items,

      savedAt:
        now,

      updatedAt:
        now,

      meta: {
        ...(
          parsed?.meta ||
          {}
        ),

        etag:
          null,
      },
    };
  }

  window.localStorage.setItem(
    cacheKey,
    JSON.stringify(
      nextValue
    )
  );
}

function patchTrailerSummaryCaches(
  trailer
) {
  if (
    typeof window ===
      "undefined" ||
    !trailer ||
    typeof trailer !==
      "object" ||
    trailer.id === null ||
    trailer.id ===
      undefined
  ) {
    return;
  }

  const trailerId =
    String(
      trailer.id
    );

  try {
    const cacheKeys =
      [];

    for (
      let index = 0;
      index <
      window.localStorage.length;
      index += 1
    ) {
      const key =
        window.localStorage.key(
          index
        );

      if (
        key &&
        key.endsWith(
          TRAILER_SUMMARY_CACHE_SUFFIX
        )
      ) {
        cacheKeys.push(
          key
        );
      }
    }

    cacheKeys.forEach(
      (
        cacheKey
      ) => {
        const parsed =
          safeParse(
            window.localStorage.getItem(
              cacheKey
            )
          );

        const currentItems =
          getSummaryItems(
            parsed
          );

        const existingIndex =
          currentItems.findIndex(
            (
              item
            ) => {
              return (
                String(
                  item?.id
                ) ===
                trailerId
              );
            }
          );

        const nextItems =
          [
            ...currentItems,
          ];

        if (
          existingIndex >=
          0
        ) {
          nextItems[
            existingIndex
          ] = {
            ...nextItems[
              existingIndex
            ],
            ...trailer,
          };
        } else {
          nextItems.unshift(
            trailer
          );
        }

        nextItems.sort(
          (
            first,
            second
          ) => {
            return String(
              first?.trailerPlate ||
              first?.trailerNumber ||
              ""
            ).localeCompare(
              String(
                second?.trailerPlate ||
                second?.trailerNumber ||
                ""
              ),
              undefined,
              {
                sensitivity:
                  "base",
              }
            );
          }
        );

        writeSummaryCacheItems(
          cacheKey,
          parsed,
          nextItems
        );
      }
    );
  } catch {
    // O detalhe continua disponível mesmo que a lista não seja atualizada.
  }

  window.dispatchEvent(
    new CustomEvent(
      TRAILER_SUMMARY_UPDATED_EVENT,
      {
        detail: {
          trailer,
        },
      }
    )
  );
}

/* =========================================================
   REACT SELECT STYLES
========================================================= */

function getCreateTrailerSelectStyles(
  hasError
) {
  return {
    container: (
      base
    ) => ({
      ...base,
      width:
        "100%",
    }),

    control: (
      base
    ) => ({
      ...base,

      width:
        "100%",

      minHeight:
        25,

      height:
        25,

      padding:
        0,

      border:
        "none",

      borderRadius:
        0,

      backgroundColor:
        "transparent",

      boxShadow:
        "none",

      cursor:
        "pointer",

      ":hover": {
        border:
          "none",
      },
    }),

    valueContainer: (
      base
    ) => ({
      ...base,

      height:
        25,

      padding:
        0,
    }),

    input: (
      base
    ) => ({
      ...base,

      margin:
        0,

      padding:
        0,

      color:
        "#f1f3f7",
    }),

    singleValue: (
      base
    ) => ({
      ...base,

      margin:
        0,

      color:
        "#f1f3f7",

      fontSize:
        12,

      fontWeight:
        650,
    }),

    placeholder: (
      base
    ) => ({
      ...base,

      margin:
        0,

      color:
        "#747b87",

      fontSize:
        12,
    }),

    indicatorsContainer: (
      base
    ) => ({
      ...base,

      height:
        25,

      display:
        "flex",

      alignItems:
        "center",

      transform:
        "translateY(-2px)",
    }),

    indicatorSeparator: (
      base
    ) => ({
      ...base,

      height:
        18,

      marginTop:
        0,

      marginBottom:
        0,

      alignSelf:
        "center",

      backgroundColor:
        hasError
          ? "rgba(239, 102, 107, 0.46)"
          : "rgba(255, 255, 255, 0.1)",
    }),

    dropdownIndicator: (
      base,
      state
    ) => ({
      ...base,

      height:
        25,

      padding:
        "0 6px",

      display:
        "flex",

      alignItems:
        "center",

      justifyContent:
        "center",

      color:
        hasError
          ? "#ef777c"
          : state.isFocused
          ? "#b8c3d3"
          : "#858d99",

      ":hover": {
        color:
          hasError
            ? "#ff9ca0"
            : "#d8dde5",
      },
    }),

    clearIndicator: (
      base
    ) => ({
      ...base,

      height:
        25,

      padding:
        "0 6px",

      display:
        "flex",

      alignItems:
        "center",

      justifyContent:
        "center",

      color:
        "#757d89",

      ":hover": {
        color:
          "#e88d96",
      },
    }),

    menu: (
      base
    ) => ({
      ...base,

      zIndex:
        9999,

      marginTop:
        8,

      overflow:
        "hidden",

      border:
        "1px solid rgba(255, 255, 255, 0.1)",

      borderRadius:
        10,

      backgroundColor:
        "#111318",

      boxShadow:
        "0 20px 48px rgba(0, 0, 0, 0.48)",
    }),

    menuPortal: (
      base
    ) => ({
      ...base,

      zIndex:
        99999,
    }),

    menuList: (
      base
    ) => ({
      ...base,

      maxHeight:
        260,

      padding:
        5,
    }),

    option: (
      base,
      state
    ) => ({
      ...base,

      margin:
        "2px 0",

      padding:
        "8px 10px",

      borderRadius:
        7,

      cursor:
        "pointer",

      color:
        state.isSelected
          ? "#ffffff"
          : "#c8ced8",

      backgroundColor:
        state.isSelected
          ? "rgba(38, 116, 231, 0.28)"
          : state.isFocused
          ? "rgba(255, 255, 255, 0.055)"
          : "transparent",

      fontSize:
        11,

      ":active": {
        backgroundColor:
          "rgba(38, 116, 231, 0.22)",
      },
    }),
  };
}

/* =========================================================
   SMALL COMPONENTS
========================================================= */

function SectionHeading({
  number,
  title,
}) {
  return (
    <div className="create-trailer-sectionHeading">
      <h2>
        {number}. {title}
      </h2>
    </div>
  );
}

function FieldCard({
  label,
  icon,
  error,
  className = "",
  children,
}) {
  return (
    <div
      className={[
        "create-trailer-fieldCard",
        error
          ? "has-error"
          : "",
        className,
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >
      <span className="create-trailer-fieldCard-icon">
        {icon}
      </span>

      <div className="create-trailer-fieldCard-body">
        <div className="create-trailer-fieldCard-label">
          {label}
        </div>

        <div className="create-trailer-fieldCard-control">
          {children}
        </div>

        {error ? (
          <div className="create-trailer-fieldCard-error">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DocumentStatusPill({
  status,
}) {
  return (
    <span
      className={[
        "create-trailer-documentStatus",
        `create-trailer-documentStatus--${status.tone}`,
      ].join(
        " "
      )}
    >
      {status.label}
    </span>
  );
}

function DateControl({
  id,
  name,
  value,
  onChange,
  hasError,
  label,
}) {
  const inputRef =
    useRef(
      null
    );

  const openCalendar =
    () => {
      const input =
        inputRef.current;

      if (!input) {
        return;
      }

      input.focus({
        preventScroll:
          true,
      });

      try {
        if (
          typeof input.showPicker ===
          "function"
        ) {
          input.showPicker();

          return;
        }
      } catch {
        // Browser sem suporte.
      }

      input.click();
    };

  return (
    <div
      className={[
        "create-trailer-dateControl",
        hasError
          ? "has-error"
          : "",
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="date"
        value={
          value ||
          ""
        }
        onChange={
          onChange
        }
      />

      <button
        type="button"
        className="create-trailer-dateControl-button"
        aria-label={
          label
        }
        title={
          label
        }
        onMouseDown={(
          event
        ) => {
          event.preventDefault();
        }}
        onClick={
          openCalendar
        }
      >
        <FaCalendarAlt />
      </button>
    </div>
  );
}

function SidebarDocumentRow({
  title,
  status,
}) {
  return (
    <div className="create-trailer-sidebarDocumentRow">
      <div className="create-trailer-sidebarDocumentName">
        <FaFileAlt />

        <span title={title}>
          {title}
        </span>
      </div>

      <DocumentStatusPill
        status={
          status
        }
      />
    </div>
  );
}

function DocumentCard({
  fieldName,
  title,
  file,
  expiryField,
  expiryValue,
  status,
  fileError,
  expiryError,
  onFileChange,
  onDateChange,
  onView,
  t,
}) {
  const inputId =
    `create-trailer-file-${fieldName}`;

  const currentFileName =
    fileName(
      file
    );

  const openFilePicker =
    () => {
      document
        .getElementById(
          inputId
        )
        ?.click();
    };

  return (
    <article
      className={[
        "create-trailer-documentCard",
        fileError ||
        expiryError
          ? "has-error"
          : "",
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >
      <header className="create-trailer-documentCard-header">
        <div className="create-trailer-documentCard-identity">
          <span className="create-trailer-documentCard-icon">
            <FaFileAlt />
          </span>

          <div className="create-trailer-documentCard-titleWrap">
            <h3 title={title}>
              {title}
            </h3>

            <span
              className="create-trailer-documentCard-fileName"
              title={
                currentFileName ||
                t(
                  "upload.no_file",
                  {
                    defaultValue:
                      "Sem ficheiro",
                  }
                )
              }
            >
              {currentFileName ||
                t(
                  "upload.no_file",
                  {
                    defaultValue:
                      "Sem ficheiro",
                  }
                )}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="create-trailer-documentCard-change"
          onClick={
            openFilePicker
          }
        >
          {file
            ? t(
                "buttons.change",
                {
                  defaultValue:
                    "Alterar",
                }
              )
            : t(
                "buttons.upload",
                {
                  defaultValue:
                    "Carregar",
                }
              )}
        </button>
      </header>

      <div className="create-trailer-documentCard-middle">
        <DocumentStatusPill
          status={
            status
          }
        />

        {file ? (
          <button
            type="button"
            className="create-trailer-documentCard-fileLink"
            title={
              currentFileName
            }
            onClick={() => {
              onView({
                file,

                title:
                  currentFileName ||
                  title,
              });
            }}
          >
            <FaEye />

            <span>
              {currentFileName}
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="create-trailer-documentCard-empty"
            onClick={
              openFilePicker
            }
          >
            <FaUpload />

            <span>
              {t(
                "upload.prompt",
                {
                  defaultValue:
                    "Carrega aqui o teu ficheiro.",
                }
              )}
            </span>
          </button>
        )}
      </div>

      <div className="create-trailer-documentCard-divider" />

      <div className="create-trailer-documentCard-validity">
        <span>
          {t(
            "labels.expiry_short",
            {
              defaultValue:
                "Validade",
            }
          )}
        </span>

        <DateControl
          id={`create-trailer-date-${expiryField}`}
          name={
            expiryField
          }
          value={
            expiryValue
          }
          onChange={
            onDateChange
          }
          hasError={
            Boolean(
              expiryError
            )
          }
          label={t(
            "labels.open_calendar",
            {
              defaultValue:
                "Abrir calendário",
            }
          )}
        />
      </div>

      {fileError ? (
        <div className="create-trailer-documentCard-error">
          {fileError}
        </div>
      ) : null}

      {expiryError ? (
        <div className="create-trailer-documentCard-error">
          {expiryError}
        </div>
      ) : null}

      <input
        type="file"
        id={inputId}
        name={
          fieldName
        }
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={
          onFileChange
        }
        className="create-trailer-hiddenFileInput"
      />
    </article>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

function CreateTrailer() {
  const {
    t,
  } =
    useTranslation(
      "create_trailer"
    );

  const navigate =
    useNavigate();

  const [
    formData,
    setFormData,
  ] = useState(
    EMPTY_FORM_DATA
  );

  const [
    files,
    setFiles,
  ] = useState(
    EMPTY_FILES
  );

  const [
    invalidFields,
    setInvalidFields,
  ] = useState(
    {}
  );

  const [
    errorMessage,
    setErrorMessage,
  ] = useState(
    ""
  );

  const [
    showSuccessPopup,
    setShowSuccessPopup,
  ] = useState(
    false
  );

  const [
    isSaving,
    setIsSaving,
  ] = useState(
    false
  );

  const [
    viewerFile,
    setViewerFile,
  ] = useState(
    null
  );

  const [
    previewOpen,
    setPreviewOpen,
  ] = useState(
    false
  );

  const navigationTimerRef =
    useRef(
      null
    );

  const typeOptions =
    useMemo(() => {
      return TRAILER_TYPES.map(
        (
          type
        ) => ({
          value:
            type,

          label:
            t(
              `types.${type}`,
              {
                defaultValue:
                  type,
              }
            ),
        })
      );
    }, [
      t,
    ]);

  const monthOptions =
    useMemo(() => {
      return Array.from(
        {
          length:
            12,
        },
        (
          _,
          index
        ) => ({
          value:
            index +
            1,

          label:
            t(
              `months.${index + 1}`,
              {
                defaultValue:
                  new Date(
                    0,
                    index
                  ).toLocaleString(
                    "default",
                    {
                      month:
                        "long",
                    }
                  ),
              }
            ),
        })
      );
    }, [
      t,
    ]);

  const yearOptions =
    useMemo(() => {
      const currentYear =
        new Date().getFullYear();

      return Array.from(
        {
          length:
            currentYear -
            1980 +
            1,
        },
        (
          _,
          index
        ) => {
          const year =
            currentYear -
            index;

          return {
            value:
              year,

            label:
              String(
                year
              ),
          };
        }
      );
    }, []);

  const selectedType =
    useMemo(() => {
      return (
        typeOptions.find(
          (
            option
          ) => {
            return (
              option.value ===
              formData.type
            );
          }
        ) ||
        null
      );
    }, [
      formData.type,
      typeOptions,
    ]);

  const selectedMonth =
    useMemo(() => {
      return (
        monthOptions.find(
          (
            option
          ) => {
            return (
              Number(
                option.value
              ) ===
              Number(
                formData.month
              )
            );
          }
        ) ||
        null
      );
    }, [
      formData.month,
      monthOptions,
    ]);

  const selectedYear =
    useMemo(() => {
      return (
        yearOptions.find(
          (
            option
          ) => {
            return (
              Number(
                option.value
              ) ===
              Number(
                formData.year
              )
            );
          }
        ) ||
        null
      );
    }, [
      formData.year,
      yearOptions,
    ]);

  const clearFieldError =
    useCallback(
      (
        field
      ) => {
        setInvalidFields(
          (
            previous
          ) => {
            if (
              !previous[
                field
              ]
            ) {
              return previous;
            }

            const next = {
              ...previous,
            };

            delete next[
              field
            ];

            return next;
          }
        );
      },
      []
    );

  const handleChange =
    useCallback(
      (
        event
      ) => {
        const {
          name,
          value,
        } =
          event.target;

        const normalizedValue =
          name ===
          "trailerPlate"
            ? normalizePlate(
                value
              )
            : value;

        setFormData(
          (
            previous
          ) => ({
            ...previous,

            [name]:
              normalizedValue,
          })
        );

        clearFieldError(
          name
        );

        setErrorMessage(
          ""
        );
      },
      [
        clearFieldError,
      ]
    );

  const handleSelectChange =
    useCallback(
      (
        name,
        option
      ) => {
        setFormData(
          (
            previous
          ) => ({
            ...previous,

            [name]:
              option?.value ??
              "",
          })
        );

        clearFieldError(
          name
        );

        setErrorMessage(
          ""
        );
      },
      [
        clearFieldError,
      ]
    );

  const handleFileChange =
    useCallback(
      (
        event
      ) => {
        const {
          name,
          files:
            selectedFiles,
        } =
          event.target;

        const selectedFile =
          selectedFiles?.[0] ||
          null;

        if (!selectedFile) {
          return;
        }

        if (
          !ALLOWED_FILE_TYPES.includes(
            selectedFile.type
          )
        ) {
          setErrorMessage(
            t(
              "errors.invalid_file_type",
              {
                defaultValue:
                  "Tipo de ficheiro inválido. Apenas JPG, PNG, WEBP ou PDF.",
              }
            )
          );

          event.target.value =
            "";

          return;
        }

        setFiles(
          (
            previous
          ) => ({
            ...previous,

            [name]:
              selectedFile,
          })
        );

        clearFieldError(
          name
        );

        setErrorMessage(
          ""
        );
      },
      [
        clearFieldError,
        t,
      ]
    );

  useEffect(() => {
    const modalOpen =
      Boolean(
        viewerFile ||
        previewOpen ||
        showSuccessPopup
      );

    if (!modalOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleKeyDown =
      (
        event
      ) => {
        if (
          event.key !==
          "Escape"
        ) {
          return;
        }

        if (
          viewerFile
        ) {
          setViewerFile(
            null
          );

          return;
        }

        if (
          previewOpen
        ) {
          setPreviewOpen(
            false
          );
        }
      };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [
    previewOpen,
    showSuccessPopup,
    viewerFile,
  ]);

  useEffect(() => {
    return () => {
      if (
        navigationTimerRef.current
      ) {
        window.clearTimeout(
          navigationTimerRef.current
        );
      }
    };
  }, []);

  const validateFields =
    useCallback(() => {
      const nextErrors =
        {};

      const numericFields = [
        "height",
        "width",
        "length",
        "weight",
        "numberOfAxles",
        "numberOfStraps",
        "numberOfRatchets",
        "numberOfWoodenBeams",
        "numberOfEdgeProtectors",
        "numberOfAntiSlipMats",
        "numberOfSidebars",
        "numberOfFireExtinguishers",
      ];

      numericFields.forEach(
        (
          field
        ) => {
          const value =
            formData[
              field
            ];

          if (
            value !== "" &&
            value !== null &&
            value !==
              undefined &&
            Number(
              value
            ) < 0
          ) {
            nextErrors[
              field
            ] =
              t(
                "errors.non_negative",
                {
                  defaultValue:
                    "O valor não pode ser negativo.",
                }
              );
          }
        }
      );

      setInvalidFields(
        nextErrors
      );

      return (
        Object.keys(
          nextErrors
        ).length ===
        0
      );
    }, [
      formData,
      t,
    ]);

  const primeCreatedTrailerCaches =
    useCallback(
      async (
        responseData
      ) => {
        const returnedTrailer =
          unwrapCreatedTrailer(
            responseData
          );

        let trailerId =
          returnedTrailer?.id ??
          returnedTrailer?.trailerId ??
          responseData?.id ??
          responseData?.trailerId ??
          responseData?.data?.id ??
          responseData?.data?.trailerId ??
          null;

        let summaryTrailer =
          null;

        try {
          const {
            data,
          } =
            await axios.get(
              "/trailers/summary",
              {
                params: {
                  _fresh:
                    Date.now(),
                },

                headers: {
                  "Cache-Control":
                    "no-cache",

                  Pragma:
                    "no-cache",
                },
              }
            );

          summaryTrailer =
            findCreatedTrailerInSummary(
              data,
              formData.trailerPlate
            );

          trailerId =
            trailerId ??
            summaryTrailer?.id ??
            null;
        } catch (
          error
        ) {
          console.warn(
            "[CreateTrailer] summary cache refresh failed:",
            error
          );
        }

        let detailTrailer =
          null;

        if (
          trailerId
        ) {
          try {
            const {
              data,
            } =
              await axios.get(
                `/trailers/${trailerId}`,
                {
                  params: {
                    _fresh:
                      Date.now(),
                  },

                  headers: {
                    "Cache-Control":
                      "no-cache",

                    Pragma:
                      "no-cache",
                  },
                }
              );

            detailTrailer =
              data;
          } catch (
            error
          ) {
            console.warn(
              "[CreateTrailer] detail cache refresh failed:",
              error
            );
          }
        }

        if (
          !trailerId
        ) {
          return;
        }

        const cachedTrailer = {
          ...formData,
          ...returnedTrailer,
          ...(
            summaryTrailer ||
            {}
          ),
          ...(
            detailTrailer ||
            {}
          ),

          id:
            Number(
              trailerId
            ),

          trailerPlate:
            detailTrailer?.trailerPlate ??
            detailTrailer?.trailerNumber ??
            summaryTrailer?.trailerPlate ??
            summaryTrailer?.trailerNumber ??
            returnedTrailer?.trailerPlate ??
            returnedTrailer?.trailerNumber ??
            normalizePlate(
              formData.trailerPlate
            ),

          trailerNumber:
            detailTrailer?.trailerNumber ??
            detailTrailer?.trailerPlate ??
            summaryTrailer?.trailerNumber ??
            summaryTrailer?.trailerPlate ??
            returnedTrailer?.trailerNumber ??
            returnedTrailer?.trailerPlate ??
            normalizePlate(
              formData.trailerPlate
            ),

          brand:
            detailTrailer?.brand ??
            summaryTrailer?.brand ??
            returnedTrailer?.brand ??
            formData.brand,

          type:
            detailTrailer?.type ??
            summaryTrailer?.type ??
            returnedTrailer?.type ??
            formData.type,

          month:
            detailTrailer?.month ??
            detailTrailer?.Month ??
            summaryTrailer?.month ??
            summaryTrailer?.Month ??
            returnedTrailer?.month ??
            returnedTrailer?.Month ??
            formData.month,

          Month:
            detailTrailer?.Month ??
            detailTrailer?.month ??
            summaryTrailer?.Month ??
            summaryTrailer?.month ??
            returnedTrailer?.Month ??
            returnedTrailer?.month ??
            formData.month,

          year:
            detailTrailer?.year ??
            summaryTrailer?.year ??
            returnedTrailer?.year ??
            formData.year,

          status:
            detailTrailer?.status ??
            summaryTrailer?.status ??
            returnedTrailer?.status ??
            "ACTIVE",

          height:
            detailTrailer?.height ??
            formData.height,

          width:
            detailTrailer?.width ??
            formData.width,

          length:
            detailTrailer?.length ??
            formData.length,

          weight:
            detailTrailer?.weight ??
            formData.weight,

          numberOfAxles:
            detailTrailer?.numberOfAxles ??
            formData.numberOfAxles,

          numberOfStraps:
            detailTrailer?.numberOfStraps ??
            formData.numberOfStraps,

          numberOfRatchets:
            detailTrailer?.numberOfRatchets ??
            formData.numberOfRatchets,

          numberOfWoodenBeams:
            detailTrailer?.numberOfWoodenBeams ??
            formData.numberOfWoodenBeams,

          numberOfEdgeProtectors:
            detailTrailer?.numberOfEdgeProtectors ??
            formData.numberOfEdgeProtectors,

          numberOfAntiSlippingMattresses:
            detailTrailer?.numberOfAntiSlippingMattresses ??
            detailTrailer?.numberOfAntiSlipMats ??
            formData.numberOfAntiSlipMats,

          numberOfAntiSlipMats:
            detailTrailer?.numberOfAntiSlipMats ??
            detailTrailer?.numberOfAntiSlippingMattresses ??
            formData.numberOfAntiSlipMats,

          numberOfSidebars:
            detailTrailer?.numberOfSidebars ??
            formData.numberOfSidebars,

          numberOfFireExtinguishers:
            detailTrailer?.numberOfFireExtinguishers ??
            formData.numberOfFireExtinguishers,

          inspectionExpiry:
            detailTrailer?.inspectionExpiry ??
            formData.inspectionExpiry,

          vehicleRegistrationCertificateExpiry:
            detailTrailer?.vehicleRegistrationCertificateExpiry ??
            formData.vehicleRegistrationCertificateExpiry,

          trailerInsuranceExpiry:
            detailTrailer?.trailerInsuranceExpiry ??
            formData.trailerInsuranceExpiry,
        };

        writeTrailerDetailsCache(
          trailerId,
          cachedTrailer
        );

        patchTrailerSummaryCaches(
          cachedTrailer
        );
      },
      [
        formData,
      ]
    );

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        isSaving
      ) {
        return;
      }

      setErrorMessage(
        ""
      );

      setShowSuccessPopup(
        false
      );

      const isValid =
        validateFields();

      if (
        !isValid
      ) {
        setErrorMessage(
          t(
            "errors.check_fields",
            {
              defaultValue:
                "❌ Verifica os campos assinalados.",
            }
          )
        );

        return;
      }

      setIsSaving(
        true
      );

      try {
        const payload =
          new FormData();

        Object.entries(
          formData
        ).forEach(
          ([
            key,
            value,
          ]) => {
            const normalizedValue =
              typeof value ===
                "string"
                ? value.trim()
                : value;

            if (
              normalizedValue !==
                "" &&
              normalizedValue !==
                null &&
              normalizedValue !==
                undefined
            ) {
              payload.append(
                key,
                normalizedValue
              );
            }
          }
        );

        Object.entries(
          files
        ).forEach(
          ([
            key,
            file,
          ]) => {
            if (
              file
            ) {
              payload.append(
                key,
                file
              );
            }
          }
        );

        const response =
          await axios.post(
            "/trailers",
            payload,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        await primeCreatedTrailerCaches(
          response.data
        );

        setShowSuccessPopup(
          true
        );

        navigationTimerRef.current =
          window.setTimeout(
            () => {
              navigate(
                "/trucks?view=trailers"
              );
            },
            1200
          );
      } catch (
        error
      ) {
        console.error(
          "[CreateTrailer] creation failed:",
          error?.response?.data ||
          error
        );

        const responseMessage =
          String(
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            ""
          );

        setErrorMessage(
          responseMessage ||
          t(
            "errors.create_fail",
            {
              defaultValue:
                "Falha ao criar o reboque. Tenta novamente.",
            }
          )
        );
      } finally {
        setIsSaving(
          false
        );
      }
    };

  const documentItems =
    useMemo(() => {
      const items = [
        {
          key:
            "inspection",

          title:
            t(
              "labels.inspection_photo",
              {
                defaultValue:
                  "Inspeção (Foto/Doc.)",
              }
            ),

          expiryField:
            "inspectionExpiry",
        },

        {
          key:
            "vehicleRegistrationCertificate",

          title:
            t(
              "labels.vehicle_registration_certificate",
              {
                defaultValue:
                  "Certificado de Matrícula",
              }
            ),

          expiryField:
            "vehicleRegistrationCertificateExpiry",
        },

        {
          key:
            "trailerInsurance",

          title:
            t(
              "labels.trailer_insurance",
              {
                defaultValue:
                  "Seguro do Reboque",
              }
            ),

          expiryField:
            "trailerInsuranceExpiry",
        },
      ];

      return items.map(
        (
          item
        ) => ({
          ...item,

          file:
            files[
              item.key
            ],

          expiryValue:
            formData[
              item.expiryField
            ],

          status:
            getDocumentStatus(
              files[
                item.key
              ],
              formData[
                item.expiryField
              ],
              t
            ),
        })
      );
    }, [
      files,
      formData,
      t,
    ]);

  const selectedTypeLabel =
    selectedType?.label ||
    "—";

  const selectedMonthLabel =
    selectedMonth?.label ||
    "—";

  const selectedYearLabel =
    selectedYear?.label ||
    formData.year ||
    "—";

  const brandTypeLabel =
    [
      formData.brand,
      selectedTypeLabel !==
        "—"
        ? selectedTypeLabel
        : "",
    ]
      .filter(
        Boolean
      )
      .join(
        " • "
      ) ||
    "—";

  const dimensionsLabel =
    [
      formData.length
        ? `${formData.length} m`
        : "—",

      formData.width
        ? `${formData.width} m`
        : "—",

      formData.height
        ? `${formData.height} m`
        : "—",
    ].join(
      " × "
    );

  const sidebarSpecifications =
    useMemo(() => {
      return [
        {
          icon:
            FaCalendarAlt,

          label:
            t(
              "labels.year",
              {
                defaultValue:
                  "Ano",
              }
            ),

          value:
            selectedYearLabel,
        },

        {
          icon:
            FaCalendarAlt,

          label:
            t(
              "labels.month",
              {
                defaultValue:
                  "Mês",
              }
            ),

          value:
            selectedMonthLabel,
        },

        {
          icon:
            FaRulerCombined,

          label:
            t(
              "sections.dimensions",
              {
                defaultValue:
                  "Dimensões",
              }
            ),

          value:
            dimensionsLabel,
        },

        {
          icon:
            FaWeightHanging,

          label:
            t(
              "labels.weight_tons",
              {
                defaultValue:
                  "Peso",
              }
            ),

          value:
            formData.weight
              ? `${formData.weight} t`
              : "—",
        },

        {
          icon:
            FaRoad,

          label:
            t(
              "labels.number_of_axles",
              {
                defaultValue:
                  "N.º de Eixos",
              }
            ),

          value:
            formData.numberOfAxles ||
            "0",
        },

        {
          icon:
            FaLink,

          label:
            t(
              "labels.number_of_straps",
              {
                defaultValue:
                  "N.º de Cintas",
              }
            ),

          value:
            formData.numberOfStraps ||
            "0",
        },

        {
          icon:
            FaLink,

          label:
            t(
              "labels.number_of_ratchets",
              {
                defaultValue:
                  "N.º de Catracas",
              }
            ),

          value:
            formData.numberOfRatchets ||
            "0",
        },

        {
          icon:
            FaBars,

          label:
            t(
              "labels.number_of_wooden_beams",
              {
                defaultValue:
                  "N.º de Vigas de Madeira",
              }
            ),

          value:
            formData.numberOfWoodenBeams ||
            "0",
        },

        {
          icon:
            FaShieldAlt,

          label:
            t(
              "labels.number_of_edge_protectors",
              {
                defaultValue:
                  "N.º de Protetores de Canto",
              }
            ),

          value:
            formData.numberOfEdgeProtectors ||
            "0",
        },

        {
          icon:
            FaLayerGroup,

          label:
            t(
              "labels.number_of_anti_slip_mats",
              {
                defaultValue:
                  "N.º de Tapetes Antiderrapantes",
              }
            ),

          value:
            formData.numberOfAntiSlipMats ||
            "0",
        },

        {
          icon:
            FaBars,

          label:
            t(
              "labels.number_of_sidebars",
              {
                defaultValue:
                  "N.º de Barras Laterais",
              }
            ),

          value:
            formData.numberOfSidebars ||
            "0",
        },

        {
          icon:
            FaFireExtinguisher,

          label:
            t(
              "labels.number_of_fire_extinguishers",
              {
                defaultValue:
                  "N.º de Extintores",
              }
            ),

          value:
            formData.numberOfFireExtinguishers ||
            "0",
        },
      ];
    }, [
      dimensionsLabel,
      formData,
      selectedMonthLabel,
      selectedYearLabel,
      t,
    ]);

  const viewerUrl =
    useMemo(() => {
      if (
        !viewerFile?.file
      ) {
        return "";
      }

      try {
        return URL.createObjectURL(
          viewerFile.file
        );
      } catch {
        return "";
      }
    }, [
      viewerFile,
    ]);

  useEffect(() => {
    return () => {
      if (
        viewerUrl?.startsWith(
          "blob:"
        )
      ) {
        try {
          URL.revokeObjectURL(
            viewerUrl
          );
        } catch {
          // URL já libertado.
        }
      }
    };
  }, [
    viewerUrl,
  ]);

  const handleDownloadViewerFile =
    () => {
      if (
        !viewerFile?.file ||
        !viewerUrl
      ) {
        return;
      }

      const link =
        document.createElement(
          "a"
        );

      link.href =
        viewerUrl;

      link.download =
        cleanFileName(
          viewerFile.title ||
          fileName(
            viewerFile.file
          ),
          "document"
        );

      link.style.display =
        "none";

      document.body.appendChild(
        link
      );

      link.click();
      link.remove();
    };

  const goBack =
    () => {
      navigate(
        "/trucks?view=trailers"
      );
    };

  return (
    <div className="create-trailer-page">
      <div className="create-trailer-shell">
        <header className="create-trailer-topbar">
          <button
            type="button"
            className="create-trailer-backButton"
            onClick={
              goBack
            }
          >
            <FaArrowLeft />

            <span>
              {t(
                "back",
                {
                  defaultValue:
                    "Voltar",
                }
              )}
            </span>
          </button>

          <div className="create-trailer-topbar-title">
            <h1>
              {t(
                "title",
                {
                  defaultValue:
                    "Criar Reboque",
                }
              )}
            </h1>

            <p>
              {t(
                "page.subtitle",
                {
                  defaultValue:
                    "Registe a informação, dimensões, equipamento e documentos do novo reboque.",
                }
              )}
            </p>
          </div>

          <div className="create-trailer-topbar-actions">
            <button
              type="button"
              className="create-trailer-cancelButton"
              disabled={
                isSaving
              }
              onClick={
                goBack
              }
            >
              {t(
                "buttons.cancel",
                {
                  defaultValue:
                    "Cancelar",
                }
              )}
            </button>

            <button
              type="button"
              className="create-trailer-previewButton"
              onClick={() => {
                setPreviewOpen(
                  true
                );
              }}
            >
              <FaEye />

              <span>
                {t(
                  "buttons.preview",
                  {
                    defaultValue:
                      "Pré-visualizar",
                  }
                )}
              </span>
            </button>

            <button
              type="submit"
              form="create-trailer-form"
              className="create-trailer-primaryButton"
              disabled={
                isSaving
              }
            >
              <FaPlus />

              <span>
                {isSaving
                  ? t(
                      "buttons.creating",
                      {
                        defaultValue:
                          "A criar...",
                      }
                    )
                  : t(
                      "buttons.create",
                      {
                        defaultValue:
                          "Criar Reboque",
                      }
                    )}
              </span>
            </button>
          </div>
        </header>

        {errorMessage ? (
          <div className="create-trailer-globalError">
            <FaExclamationTriangle />

            <span>
              {errorMessage}
            </span>
          </div>
        ) : null}

        <div className="create-trailer-layout">
          <aside className="create-trailer-sidebar">
            <div className="create-trailer-sidebarCard">
              <div className="create-trailer-sidebarIdentity">
                <span className="create-trailer-sidebarEyebrow">
                  {t(
                    "labels.trailer_plate",
                    {
                      defaultValue:
                        "Matrícula do Reboque",
                    }
                  )}
                </span>

                <h2>
                  {formData.trailerPlate ||
                    "—"}
                </h2>

                <p>
                  {brandTypeLabel}
                </p>

                <span className="create-trailer-newStatus">
                  <span />

                  {t(
                    "status.new",
                    {
                      defaultValue:
                        "Novo registo",
                    }
                  )}
                </span>
              </div>

              <div className="create-trailer-sidebarBlock">
                <div className="create-trailer-sidebarBlockTitle">
                  {t(
                    "labels.trailer_type",
                    {
                      defaultValue:
                        "Tipo de Reboque",
                    }
                  )}
                </div>

                <div className="create-trailer-sidebarType">
                  <FaRoad />

                  <span>
                    {selectedTypeLabel}
                  </span>
                </div>
              </div>

              <div className="create-trailer-sidebarBlock">
                <div className="create-trailer-sidebarBlockTitle">
                  {t(
                    "sections.documents",
                    {
                      defaultValue:
                        "Documentos",
                    }
                  )}
                </div>

                <div className="create-trailer-sidebarDocuments">
                  {documentItems.map(
                    (
                      item
                    ) => (
                      <SidebarDocumentRow
                        key={
                          item.key
                        }
                        title={
                          item.title
                        }
                        status={
                          item.status
                        }
                      />
                    )
                  )}
                </div>
              </div>

              <div className="create-trailer-sidebarBlock">
                <div className="create-trailer-sidebarBlockTitle">
                  {t(
                    "sections.specifications",
                    {
                      defaultValue:
                        "Especificações",
                    }
                  )}
                </div>

                <div className="create-trailer-sidebarSpecs">
                  {sidebarSpecifications.map(
                    (
                      item
                    ) => {
                      const Icon =
                        item.icon;

                      return (
                        <div
                          key={
                            item.label
                          }
                          className="create-trailer-sidebarSpecRow"
                        >
                          <span className="create-trailer-sidebarSpecLabel">
                            <Icon />

                            <span>
                              {item.label}
                            </span>
                          </span>

                          <strong
                            title={
                              String(
                                item.value
                              )
                            }
                          >
                            {item.value}
                          </strong>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </aside>

          <main className="create-trailer-main">
            <form
              id="create-trailer-form"
              className="create-trailer-dashboardForm"
              onSubmit={
                handleSubmit
              }
              encType="multipart/form-data"
            >
              <section className="create-trailer-sectionCard">
                <SectionHeading
                  number="1"
                  title={t(
                    "sections.basic",
                    {
                      defaultValue:
                        "Informação Básica",
                    }
                  )}
                />

                <div className="create-trailer-basicGrid">
                  <FieldCard
                    label={t(
                      "labels.trailer_plate",
                      {
                        defaultValue:
                          "Matrícula do Reboque",
                      }
                    )}
                    icon={<FaHashtag />}
                    error={
                      invalidFields.trailerPlate
                    }
                    className="create-trailer-span-4"
                  >
                    <input
                      type="text"
                      name="trailerPlate"
                      value={
                        formData.trailerPlate
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput create-trailer-controlInput--uppercase"
                      placeholder={t(
                        "placeholders.plate",
                        {
                          defaultValue:
                            "Introduz a matrícula...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.trailer_brand",
                      {
                        defaultValue:
                          "Marca do Reboque",
                      }
                    )}
                    icon={<FaIndustry />}
                    error={
                      invalidFields.brand
                    }
                    className="create-trailer-span-4"
                  >
                    <input
                      type="text"
                      name="brand"
                      value={
                        formData.brand
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder={t(
                        "placeholders.brand",
                        {
                          defaultValue:
                            "Introduz a marca...",
                        }
                      )}
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.trailer_type",
                      {
                        defaultValue:
                          "Tipo de Reboque",
                      }
                    )}
                    icon={<FaRoad />}
                    error={
                      invalidFields.type
                    }
                    className="create-trailer-span-4"
                  >
                    <Select
                      name="type"
                      value={
                        selectedType
                      }
                      options={
                        typeOptions
                      }
                      onChange={(
                        option
                      ) => {
                        handleSelectChange(
                          "type",
                          option
                        );
                      }}
                      placeholder={t(
                        "placeholders.select_type",
                        {
                          defaultValue:
                            "Seleciona o tipo de reboque",
                        }
                      )}
                      styles={getCreateTrailerSelectStyles(
                        Boolean(
                          invalidFields.type
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.month",
                      {
                        defaultValue:
                          "Mês",
                      }
                    )}
                    icon={<FaCalendarAlt />}
                    error={
                      invalidFields.month
                    }
                    className="create-trailer-span-6"
                  >
                    <Select
                      name="month"
                      value={
                        selectedMonth
                      }
                      options={
                        monthOptions
                      }
                      onChange={(
                        option
                      ) => {
                        handleSelectChange(
                          "month",
                          option
                        );
                      }}
                      placeholder={t(
                        "placeholders.select_month",
                        {
                          defaultValue:
                            "Seleciona o mês",
                        }
                      )}
                      styles={getCreateTrailerSelectStyles(
                        Boolean(
                          invalidFields.month
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.year",
                      {
                        defaultValue:
                          "Ano",
                      }
                    )}
                    icon={<FaCalendarAlt />}
                    error={
                      invalidFields.year
                    }
                    className="create-trailer-span-6"
                  >
                    <Select
                      name="year"
                      value={
                        selectedYear
                      }
                      options={
                        yearOptions
                      }
                      onChange={(
                        option
                      ) => {
                        handleSelectChange(
                          "year",
                          option
                        );
                      }}
                      placeholder={t(
                        "placeholders.select_year",
                        {
                          defaultValue:
                            "Seleciona o ano",
                        }
                      )}
                      styles={getCreateTrailerSelectStyles(
                        Boolean(
                          invalidFields.year
                        )
                      )}
                      isClearable
                      menuPortalTarget={
                        typeof document !==
                        "undefined"
                          ? document.body
                          : null
                      }
                      menuPosition="fixed"
                    />
                  </FieldCard>
                </div>
              </section>

              <section className="create-trailer-sectionCard">
                <SectionHeading
                  number="2"
                  title={t(
                    "sections.dimensions_equipment",
                    {
                      defaultValue:
                        "Dimensões & Equipamento",
                    }
                  )}
                />

                <div className="create-trailer-equipmentGrid">
                  <FieldCard
                    label={t(
                      "labels.height_m",
                      {
                        defaultValue:
                          "Altura (m)",
                      }
                    )}
                    icon={<FaRulerVertical />}
                    error={
                      invalidFields.height
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="height"
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        formData.height
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.width_m",
                      {
                        defaultValue:
                          "Largura (m)",
                      }
                    )}
                    icon={<FaRulerHorizontal />}
                    error={
                      invalidFields.width
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="width"
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        formData.width
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.length_m",
                      {
                        defaultValue:
                          "Comprimento (m)",
                      }
                    )}
                    icon={<FaRulerCombined />}
                    error={
                      invalidFields.length
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="length"
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        formData.length
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.weight_tons",
                      {
                        defaultValue:
                          "Peso (toneladas)",
                      }
                    )}
                    icon={<FaWeightHanging />}
                    error={
                      invalidFields.weight
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="weight"
                      type="number"
                      min="0"
                      step="0.01"
                      value={
                        formData.weight
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0.00"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_axles",
                      {
                        defaultValue:
                          "N.º de Eixos",
                      }
                    )}
                    icon={<FaRoad />}
                    error={
                      invalidFields.numberOfAxles
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfAxles"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfAxles
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_straps",
                      {
                        defaultValue:
                          "N.º de Cintas",
                      }
                    )}
                    icon={<FaLink />}
                    error={
                      invalidFields.numberOfStraps
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfStraps"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfStraps
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_ratchets",
                      {
                        defaultValue:
                          "N.º de Catracas",
                      }
                    )}
                    icon={<FaLink />}
                    error={
                      invalidFields.numberOfRatchets
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfRatchets"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfRatchets
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_wooden_beams",
                      {
                        defaultValue:
                          "N.º de Vigas de Madeira",
                      }
                    )}
                    icon={<FaBars />}
                    error={
                      invalidFields.numberOfWoodenBeams
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfWoodenBeams"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfWoodenBeams
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_edge_protectors",
                      {
                        defaultValue:
                          "N.º de Protetores de Canto",
                      }
                    )}
                    icon={<FaShieldAlt />}
                    error={
                      invalidFields.numberOfEdgeProtectors
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfEdgeProtectors"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfEdgeProtectors
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_anti_slip_mats",
                      {
                        defaultValue:
                          "N.º de Tapetes Antiderrapantes",
                      }
                    )}
                    icon={<FaLayerGroup />}
                    error={
                      invalidFields.numberOfAntiSlipMats
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfAntiSlipMats"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfAntiSlipMats
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_sidebars",
                      {
                        defaultValue:
                          "N.º de Barras Laterais",
                      }
                    )}
                    icon={<FaBars />}
                    error={
                      invalidFields.numberOfSidebars
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfSidebars"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfSidebars
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>

                  <FieldCard
                    label={t(
                      "labels.number_of_fire_extinguishers",
                      {
                        defaultValue:
                          "N.º de Extintores",
                      }
                    )}
                    icon={<FaFireExtinguisher />}
                    error={
                      invalidFields.numberOfFireExtinguishers
                    }
                    className="create-trailer-span-3"
                  >
                    <input
                      name="numberOfFireExtinguishers"
                      type="number"
                      min="0"
                      step="1"
                      value={
                        formData.numberOfFireExtinguishers
                      }
                      onChange={
                        handleChange
                      }
                      className="create-trailer-controlInput"
                      placeholder="0"
                    />
                  </FieldCard>
                </div>
              </section>

              <section className="create-trailer-sectionCard create-trailer-documentsSection">
                <SectionHeading
                  number="3"
                  title={t(
                    "sections.documents_validities",
                    {
                      defaultValue:
                        "Documentos & Validades",
                    }
                  )}
                />

                <div className="create-trailer-documentsGrid">
                  {documentItems.map(
                    (
                      item
                    ) => (
                      <DocumentCard
                        key={
                          item.key
                        }
                        fieldName={
                          item.key
                        }
                        title={
                          item.title
                        }
                        file={
                          item.file
                        }
                        expiryField={
                          item.expiryField
                        }
                        expiryValue={
                          item.expiryValue
                        }
                        status={
                          item.status
                        }
                        fileError={
                          invalidFields[
                            item.key
                          ]
                        }
                        expiryError={
                          invalidFields[
                            item.expiryField
                          ]
                        }
                        onFileChange={
                          handleFileChange
                        }
                        onDateChange={
                          handleChange
                        }
                        onView={
                          setViewerFile
                        }
                        t={
                          t
                        }
                      />
                    )
                  )}
                </div>
              </section>
            </form>
          </main>
        </div>
      </div>

      {showSuccessPopup ? (
        <div className="create-trailer-successPopup-overlay">
          <div className="create-trailer-successPopup">
            <div className="create-trailer-successPopup-icon">
              <FaCheckCircle />
            </div>

            <h3>
              {t(
                "success.createdTitle",
                {
                  defaultValue:
                    "Reboque criado",
                }
              )}
            </h3>

            <p>
              {t(
                "messages.created_success",
                {
                  defaultValue:
                    "Reboque criado com sucesso!",
                }
              )}
            </p>

            <button
              type="button"
              className="create-trailer-successPopup-btn"
              onClick={
                goBack
              }
            >
              {t(
                "buttons.ok",
                {
                  defaultValue:
                    "OK",
                }
              )}
            </button>
          </div>
        </div>
      ) : null}

      {previewOpen ? (
        <div
          className="create-trailer-previewOverlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-trailer-preview-title"
          onClick={() => {
            setPreviewOpen(
              false
            );
          }}
        >
          <div
            className="create-trailer-previewModal"
            onClick={(
              event
            ) => {
              event.stopPropagation();
            }}
          >
            <header className="create-trailer-previewHeader">
              <div>
                <span>
                  {t(
                    "preview.eyebrow",
                    {
                      defaultValue:
                        "Pré-visualização",
                    }
                  )}
                </span>

                <h2 id="create-trailer-preview-title">
                  {formData.trailerPlate ||
                    t(
                      "preview.no_plate",
                      {
                        defaultValue:
                          "Reboque sem matrícula",
                      }
                    )}
                </h2>

                <p>
                  {brandTypeLabel}
                </p>
              </div>

              <button
                type="button"
                className="create-trailer-previewClose"
                aria-label={t(
                  "buttons.close",
                  {
                    defaultValue:
                      "Fechar",
                  }
                )}
                onClick={() => {
                  setPreviewOpen(
                    false
                  );
                }}
              >
                ×
              </button>
            </header>

            <div className="create-trailer-previewBody">
              <section className="create-trailer-previewSection">
                <h3>
                  {t(
                    "sections.dimensions",
                    {
                      defaultValue:
                        "Dimensões",
                    }
                  )}
                </h3>

                <div className="create-trailer-previewStats">
                  <div>
                    <span>
                      {t(
                        "labels.length_m",
                        {
                          defaultValue:
                            "Comprimento",
                        }
                      )}
                    </span>

                    <strong>
                      {formData.length
                        ? `${formData.length} m`
                        : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t(
                        "labels.width_m",
                        {
                          defaultValue:
                            "Largura",
                        }
                      )}
                    </span>

                    <strong>
                      {formData.width
                        ? `${formData.width} m`
                        : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t(
                        "labels.height_m",
                        {
                          defaultValue:
                            "Altura",
                        }
                      )}
                    </span>

                    <strong>
                      {formData.height
                        ? `${formData.height} m`
                        : "—"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      {t(
                        "labels.weight_tons",
                        {
                          defaultValue:
                            "Peso",
                        }
                      )}
                    </span>

                    <strong>
                      {formData.weight
                        ? `${formData.weight} t`
                        : "—"}
                    </strong>
                  </div>
                </div>
              </section>

              <section className="create-trailer-previewSection">
                <h3>
                  {t(
                    "sections.documents",
                    {
                      defaultValue:
                        "Documentos",
                    }
                  )}
                </h3>

                <div className="create-trailer-previewDocuments">
                  {documentItems.map(
                    (
                      item
                    ) => (
                      <div
                        key={
                          item.key
                        }
                        className="create-trailer-previewDocument"
                      >
                        <span title={item.title}>
                          {item.title}
                        </span>

                        <DocumentStatusPill
                          status={
                            item.status
                          }
                        />

                        <strong>
                          {item.expiryValue ||
                            "—"}
                        </strong>
                      </div>
                    )
                  )}
                </div>
              </section>

              <section className="create-trailer-previewSection">
                <h3>
                  {t(
                    "sections.equipment",
                    {
                      defaultValue:
                        "Equipamento",
                    }
                  )}
                </h3>

                <div className="create-trailer-previewEquipment">
                  {sidebarSpecifications
                    .slice(
                      4
                    )
                    .map(
                      (
                        item
                      ) => (
                        <div
                          key={
                            item.label
                          }
                        >
                          <span>
                            {item.label}
                          </span>

                          <strong>
                            {item.value}
                          </strong>
                        </div>
                      )
                    )}
                </div>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {viewerFile && viewerUrl ? (
        <div
          className="create-trailer-viewerOverlay"
          onClick={() => {
            setViewerFile(
              null
            );
          }}
        >
          <div
            className="create-trailer-viewerModal"
            onClick={(
              event
            ) => {
              event.stopPropagation();
            }}
          >
            <div className="create-trailer-viewerHeader">
              <span
                className="create-trailer-viewerTitle"
                title={
                  viewerFile.title
                }
              >
                {viewerFile.title}
              </span>

              <div className="create-trailer-viewerActions">
                <button
                  type="button"
                  className="create-trailer-viewerActionBtn"
                  aria-label={t(
                    "buttons.download",
                    {
                      defaultValue:
                        "Descarregar",
                    }
                  )}
                  title={t(
                    "buttons.download",
                    {
                      defaultValue:
                        "Descarregar",
                    }
                  )}
                  onClick={
                    handleDownloadViewerFile
                  }
                >
                  <FaDownload />
                </button>

                <button
                  type="button"
                  className="create-trailer-viewerClose"
                  aria-label={t(
                    "buttons.close",
                    {
                      defaultValue:
                        "Fechar",
                    }
                  )}
                  title={t(
                    "buttons.close",
                    {
                      defaultValue:
                        "Fechar",
                    }
                  )}
                  onClick={() => {
                    setViewerFile(
                      null
                    );
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="create-trailer-viewerBody">
              {isPdfFile(
                viewerFile.file
              ) ? (
                <iframe
                  title={
                    viewerFile.title ||
                    "document"
                  }
                  src={
                    viewerUrl
                  }
                  className="create-trailer-viewerFrame"
                />
              ) : isImageFile(
                  viewerFile.file
                ) ? (
                <img
                  src={
                    viewerUrl
                  }
                  alt={
                    viewerFile.title ||
                    "document"
                  }
                  className="create-trailer-viewerImage"
                />
              ) : (
                <div className="create-trailer-viewerUnsupported">
                  <FaFileAlt />

                  <p>
                    {viewerFile.title}
                  </p>

                  <button
                    type="button"
                    className="create-trailer-viewerDownloadFallback"
                    onClick={
                      handleDownloadViewerFile
                    }
                  >
                    <FaDownload />

                    {t(
                      "buttons.download",
                      {
                        defaultValue:
                          "Descarregar",
                      }
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CreateTrailer;